import React, { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import {
  Box,
  Chip,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import dayjs from "dayjs";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import LoaderOverlay from "../../../../../views/components/common/pages/AppLoader";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { SortType } from "../../../../../models/SortType";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import {
  AccountRow,
  AccountSortBy,
  CategoryNode,
} from "../../../../../models/Dashboard/AccountManagement/AccountManagement";
import {
  AccountManagementFilters,
  defaultAccountManagementFilters,
} from "../../../../../models/Dashboard/AccountManagement/AccountManagementFilters";
import { AccountManagementStore } from "../../../stores/AccountManagementStore";
import AccountManagementTable from "../../components/tables/AccountManagementTable";
import AccountQuarterOrdersDrawer from "../../components/dashboard/AccountQuarterOrdersDrawer";

type AppliedFilterType = "search" | "poc" | "category" | "subcategory";

type AppliedFilterChip = {
  type: AppliedFilterType;
  id: string;
  label: string;
};

const parseOptionalNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const buildCategoryNameMap = (categories: CategoryNode[]) => {
  const map = new Map<number, string>();
  const walk = (nodes: CategoryNode[]) => {
    nodes.forEach((node) => {
      map.set(node.id, node.name);
      if (node.children?.length) {
        walk(node.children);
      }
    });
  };
  walk(categories);
  return map;
};

const escapeCsvValue = (value: string | number | null | undefined) =>
  `"${String(value ?? "").replace(/"/g, '""')}"`;

const formatCsvDate = (value: string | null) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "-";
};

const formatCsvMoney = (value: AccountRow[keyof AccountRow]) => {
  if (value == null || value === "") return "-";
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? String(parsed) : "-";
};

const QUARTER_KEYS = (() => {
  const keys: Array<keyof AccountRow> = [];
  [21, 22, 23, 24, 25, 26].forEach((year) => {
    [1, 2, 3, 4].forEach((quarter) => {
      keys.push(`fy${year}_q${quarter}` as keyof AccountRow);
    });
  });
  return keys;
})();

const AccountManagementDashboard: React.FC = () => {
  const theme = useTheme();
  const { userStore } = useStores();
  const { showError } = useNotification();
  const [store] = useState(() => new AccountManagementStore());
  const [isSearchExpanded, setIsSearchExpanded] = useState(true);

  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, 400);
  const [minLtv, setMinLtv] = useState("");
  const [maxLtv, setMaxLtv] = useState("");
  const [minYearRev, setMinYearRev] = useState("");
  const [maxYearRev, setMaxYearRev] = useState("");

  const loading = store.loading || store.filtersLoading;
  const filters = store.filters;

  const salesPocOptions = useMemo<FilterValue[]>(
    () =>
      store.salesPocs.map((poc) => ({
        id: String(poc.id),
        value: poc.value,
      })),
    [store.salesPocs],
  );

  const categoryOptions = useMemo<FilterValue[]>(
    () =>
      store.categories.map((category) => ({
        id: String(category.id),
        value: category.name,
      })),
    [store.categories],
  );

  const subcategoryOptions = useMemo<FilterValue[]>(() => {
    if (filters.category_id == null) return [];
    const selectedCategory = store.categories.find(
      (category) => category.id === filters.category_id,
    );
    return (selectedCategory?.children ?? []).map((child) => ({
      id: String(child.id),
      value: child.name,
    }));
  }, [filters.category_id, store.categories]);

  const selectedPoc = useMemo<FilterValue[]>(
    () =>
      filters.poc_id != null
        ? salesPocOptions.filter(
          (option) => option.id === String(filters.poc_id),
        )
        : [],
    [filters.poc_id, salesPocOptions],
  );

  const selectedCategory = useMemo<FilterValue[]>(
    () =>
      filters.category_id != null
        ? categoryOptions.filter(
          (option) => option.id === String(filters.category_id),
        )
        : [],
    [filters.category_id, categoryOptions],
  );

  const selectedSubcategory = useMemo<FilterValue[]>(
    () =>
      filters.subcategory_id != null
        ? subcategoryOptions.filter(
          (option) => option.id === String(filters.subcategory_id),
        )
        : [],
    [filters.subcategory_id, subcategoryOptions],
  );

  // const selectedYearBasis = useMemo<FilterValue[]>(
  //   () =>
  //     YEAR_BASIS_OPTIONS.filter((option) => option.id === filters.year_basis),
  //   [filters.year_basis],
  // );

  const appliedFilterChips = useMemo((): AppliedFilterChip[] => {
    const chips: AppliedFilterChip[] = [];

    if (filters.q.trim()) {
      chips.push({
        type: "search",
        id: filters.q.trim(),
        label: `Search: ${filters.q.trim()}`,
      });
    }

    if (selectedPoc[0]) {
      chips.push({
        type: "poc",
        id: selectedPoc[0].id,
        label: `POC: ${selectedPoc[0].value}`,
      });
    }

    if (selectedCategory[0]) {
      chips.push({
        type: "category",
        id: selectedCategory[0].id,
        label: `Category: ${selectedCategory[0].value}`,
      });
    }

    if (selectedSubcategory[0]) {
      chips.push({
        type: "subcategory",
        id: selectedSubcategory[0].id,
        label: `Subcategory: ${selectedSubcategory[0].value}`,
      });
    }

    return chips;
  }, [filters.q, selectedCategory, selectedPoc, selectedSubcategory]);

  const fetchAccounts = useCallback(
    async (currentFilters: AccountManagementFilters) => {
      try {
        store.setLoading(true);
        await store.fetchAccounts(currentFilters);
      } catch (error: any) {
        showError(
          "Error",
          error?.message || "Failed to fetch account management data",
        );
      } finally {
        store.setLoading(false);
      }
    },
    [store],
  );

  const buildFilters = useCallback(
    (
      overrides: Partial<AccountManagementFilters> = {},
    ): AccountManagementFilters => ({
      ...store.filters,
      q: searchText.trim(),
      min_ltv: parseOptionalNumber(minLtv),
      max_ltv: parseOptionalNumber(maxLtv),
      min_year_rev: parseOptionalNumber(minYearRev),
      max_year_rev: parseOptionalNumber(maxYearRev),
      ...overrides,
    }),
    [maxLtv, maxYearRev, minLtv, minYearRev, searchText, store.filters],
  );

  useEffect(() => {
    if (
      !userStore.hasPermission([
        EmployeeRoles.VIEW_ACCOUNT_MANAGEMENT_DASHBOARD,
      ])
    ) {
      return;
    }

    const init = async () => {
      try {
        await store.fetchFilterOptions();
        await fetchAccounts(store.filters);
      } catch (error: any) {
        showError(
          "Error",
          error?.message || "Failed to load account management filters",
        );
      }
    };

    void init();
  }, [fetchAccounts, store, userStore]);

  useEffect(() => {
    if (
      !userStore.hasPermission([
        EmployeeRoles.VIEW_ACCOUNT_MANAGEMENT_DASHBOARD,
      ])
    ) {
      return;
    }

    if (debouncedSearchText.trim() === store.filters.q) {
      return;
    }

    const nextFilters = buildFilters({
      q: debouncedSearchText.trim(),
      page_no: 1,
    });
    store.setFilters(nextFilters);
    void fetchAccounts(nextFilters);
  }, [buildFilters, debouncedSearchText, fetchAccounts, store, userStore]);

  const handleApplyFilters = () => {
    try {
      const nextFilters = buildFilters({ page_no: 1 });
      store.setFilters(nextFilters);
      void fetchAccounts(nextFilters);
    } catch (error: any) {
      showError("Error", error?.message || "Failed to apply filters");
    }
  };

  const handleClearFilters = () => {
    try {
      const defaults = defaultAccountManagementFilters();
      setSearchText("");
      setMinLtv("");
      setMaxLtv("");
      setMinYearRev("");
      setMaxYearRev("");
      store.resetFilters();
      void fetchAccounts(defaults);
    } catch (error: any) {
      showError("Error", error?.message || "Failed to clear filters");
    }
  };

  const handleRemoveAppliedFilter = useCallback(
    (filter: AppliedFilterChip) => {
      try {
        const nextFilters = buildFilters({ page_no: 1 });

        if (filter.type === "search") {
          setSearchText("");
          nextFilters.q = "";
        }
        if (filter.type === "poc") {
          nextFilters.poc_id = null;
        }
        if (filter.type === "category") {
          nextFilters.category_id = null;
          nextFilters.subcategory_id = null;
        }
        if (filter.type === "subcategory") {
          nextFilters.subcategory_id = null;
        }

        store.setFilters(nextFilters);
        void fetchAccounts(nextFilters);
      } catch (error: any) {
        showError("Error", error?.message || "Failed to remove filter");
      }
    },
    [buildFilters, fetchAccounts, store],
  );

  const handleDownload = () => {
    try {
      const rows = store.accounts;
      if (rows.length === 0) {
        showError("Error", "No data to export.");
        return;
      }

      const categoryNameMap = buildCategoryNameMap(store.categories);
      const headers = [
        "Customer ID",
        "Account Name",
        "Sales POC",
        "Last Order POC",
        "Category",
        "First Order Date",
        "Last Order Date",
        "Lifetime Orders",
        "LTV",
        "AOV",
        "Current FY Revenue",
        "Last FY Revenue",
        "3Y Revenue",
        "5Y Revenue",
        ...QUARTER_KEYS.map((key) =>
          key.replace("fy", "FY").replace("_q", " Q").toUpperCase(),
        ),
      ];

      const csvRows = rows.map((row) => {
        const categoryName =
          row.effective_category_id != null
            ? (categoryNameMap.get(row.effective_category_id) ?? "-")
            : "-";

        return [
          row.customer_id,
          row.account_name || "-",
          row.effective_sales_poc || "-",
          row.last_order_sales_poc || "-",
          categoryName,
          formatCsvDate(row.first_order_date),
          formatCsvDate(row.last_order_date),
          row.lifetime_orders ?? "-",
          formatCsvMoney(row.lifetime_value),
          formatCsvMoney(row.avg_order_value),
          formatCsvMoney(row.current_fy_revenue),
          formatCsvMoney(row.last_fy_revenue),
          formatCsvMoney(row.revenue_3yr),
          formatCsvMoney(row.revenue_5yr),
          ...QUARTER_KEYS.map((key) => formatCsvMoney(row[key])),
        ]
          .map(escapeCsvValue)
          .join(",");
      });

      const csvContent = [headers.join(","), ...csvRows].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const timestamp = dayjs().format("YYYY-MM-DD_HH-mm-ss");
      link.href = url;
      link.setAttribute("download", `account_management_${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      showError(
        "Error",
        error?.message || "Failed to download account management data",
      );
    }
  };

  if (
    !userStore.hasPermission([EmployeeRoles.VIEW_ACCOUNT_MANAGEMENT_DASHBOARD])
  ) {
    return <PermissionBlockLabel />;
  }

  return (
    <>
      <LoaderOverlay loading={loading} />
      <Box
        sx={{
          bgcolor: theme.palette.customColors.pageBackground,
          minHeight: "100vh",
        }}
      >
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            backgroundColor: theme.palette.customColors.white,
            border: `1px solid ${theme.palette.customColors.softCardBorder}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: 2,
              mb: 2,
            }}
          >
            <CustomTypography
              variant="heading3"
              sx={{ color: theme.palette.customColors.navyText }}
            >
              Account Management Dashboard
            </CustomTypography>
            <AppButton
              variant="primary"
              label="Download CSV"
              startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
              onClick={handleDownload}
              customStyles={{ minWidth: 148, flexShrink: 0 }}
            />
          </Box>

          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              borderRadius: 2,
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <Box
              onClick={() => setIsSearchExpanded(!isSearchExpanded)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 0.5,
                cursor: "pointer",
                borderBottom: isSearchExpanded ? "1px solid" : "none",
                borderColor: "divider",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CustomTypography variant="heading6">Search</CustomTypography>
              <IconButton size="small">
                {isSearchExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={isSearchExpanded} timeout="auto" unmountOnExit>
              <Grid2 container spacing={2} columns={12} padding={2}>
                <Grid2
                  size={{ xs: 12, sm: 9, md: 11, lg: 11 }}
                  sx={{ padding: 0 }}
                >
                  <CustomTextField
                    value={searchText}
                    placeholder="Search by account name"
                    width="100%"
                    onChange={(event) => setSearchText(event.target.value)}
                    onSearch={(query) => setSearchText(query)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        handleApplyFilters();
                      }
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 3, md: 1, lg: 1 }}>
                  <AppButton
                    variant="primary"
                    label="Search"
                    onClick={handleApplyFilters}
                    customStyles={{ width: "100%", height: 40 }}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <AppSearchDropdownMulti
                    title="Sales POC"
                    list={salesPocOptions}
                    value={selectedPoc}
                    local
                    isMultiSelect={false}
                    handleSelect={(selectedItems) => {
                      store.updateFilters((prev) => ({
                        ...prev,
                        poc_id: selectedItems?.[0]?.id
                          ? Number(selectedItems[0].id)
                          : null,
                      }));
                    }}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <AppSearchDropdownMulti
                    title="Category"
                    list={categoryOptions}
                    value={selectedCategory}
                    local
                    isMultiSelect={false}
                    handleSelect={(selectedItems) => {
                      const nextCategoryId = selectedItems?.[0]?.id
                        ? Number(selectedItems[0].id)
                        : null;
                      store.updateFilters((prev) => ({
                        ...prev,
                        category_id: nextCategoryId,
                        subcategory_id: null,
                      }));
                    }}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <AppButton
                      variant="transparentPrimary"
                      label="Clear"
                      onClick={handleClearFilters}
                      customStyles={{ width: "100%" }}
                    />
                    <AppButton
                      variant="primary"
                      label="Apply"
                      onClick={handleApplyFilters}
                      customStyles={{ width: "100%" }}
                    />
                  </Box>
                </Grid2>
                {/* 
                
                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <AppSearchDropdownMulti
                    title="Year basis"
                    list={YEAR_BASIS_OPTIONS}
                    value={selectedYearBasis}
                    local
                    isMultiSelect={false}
                    handleSelect={(selectedItems) => {
                      const nextBasis = (selectedItems?.[0]?.id ||
                        "current") as YearBasis;
                      store.updateFilters((prev) => ({
                        ...prev,
                        year_basis: nextBasis,
                      }));
                    }}
                  /> 
                </Grid2> */}

                {/* <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <CustomTextField
                    value={minLtv}
                    placeholder="Min LTV"
                    type="number"
                    width="100%"
                    onChange={(event) => setMinLtv(event.target.value)}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <CustomTextField
                    value={maxLtv}
                    placeholder="Max LTV"
                    type="number"
                    width="100%"
                    onChange={(event) => setMaxLtv(event.target.value)}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <CustomTextField
                    value={minYearRev}
                    placeholder="Min FY revenue"
                    type="number"
                    width="100%"
                    onChange={(event) => setMinYearRev(event.target.value)}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <CustomTextField
                    value={maxYearRev}
                    placeholder="Max FY revenue"
                    type="number"
                    width="100%"
                    onChange={(event) => setMaxYearRev(event.target.value)}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <DateRangePickerNew
                    startDate={filters.last_from}
                    endDate={filters.last_to}
                    onRangeSelect={(range: {
                      startDate: Dayjs | null;
                      endDate: Dayjs | null;
                    }) =>
                      store.updateFilters((prev) => ({
                        ...prev,
                        last_from: range.startDate,
                        last_to: range.endDate,
                      }))
                    }
                  />
                </Grid2> */}

                {/* <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}></Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}></Grid2>

                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <AppButton
                      variant="transparentPrimary"
                      label="Clear"
                      onClick={handleClearFilters}
                      customStyles={{ width: "100%" }}
                    />
                    <AppButton
                      variant="primary"
                      label="Apply"
                      onClick={handleApplyFilters}
                      customStyles={{ width: "100%" }}
                    />
                  </Box>
                </Grid2> 
                */}
              </Grid2>
            </Collapse>
          </Paper>

          {appliedFilterChips.length > 0 && (
            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <CustomTypography
                variant="heading7"
                color={theme.palette.customColors.tableGrayHeadingColor}
                style={{
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  marginBottom: 10,
                }}
              >
                Applied filters
              </CustomTypography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                {appliedFilterChips.map((filter) => (
                  <Chip
                    key={`${filter.type}-${filter.id}`}
                    size="small"
                    label={filter.label}
                    onDelete={() => handleRemoveAppliedFilter(filter)}
                    sx={{
                      fontSize: "0.8rem",
                      height: 28,
                      fontWeight: 500,
                      backgroundColor: theme.palette.primary.main + "14",
                      color: theme.palette.primary.main,
                      border: `1px solid ${theme.palette.primary.main}40`,
                      "& .MuiChip-deleteIcon": {
                        color: theme.palette.primary.main,
                        "&:hover": {
                          color: theme.palette.primary.dark,
                        },
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          <AccountManagementTable
            rows={store.accounts}
            categories={store.categories}
            page_no={store.pageNo}
            limit={store.limit}
            total_count={store.totalCount}
            sortBy={filters.sort_by}
            sortType={filters.sort_type}
            onSortChange={(column: AccountSortBy, sortType: SortType) => {
              const nextFilters = buildFilters({
                sort_by: column,
                sort_type: sortType,
                page_no: 1,
              });
              store.setFilters(nextFilters);
              void fetchAccounts(nextFilters);
            }}
            onPageChange={(_event, page) => {
              const nextFilters = buildFilters({ page_no: page });
              store.setFilters(nextFilters);
              void fetchAccounts(nextFilters);
            }}
            onRowsPerPageChange={(limit) => {
              const nextFilters = buildFilters({ limit, page_no: 1 });
              store.setFilters(nextFilters);
              void fetchAccounts(nextFilters);
            }}
            onQuarterClick={(params) => {
              store.openQuarterDrawer(params);
            }}
          />
        </Paper>
      </Box>
      <AccountQuarterOrdersDrawer store={store} />
    </>
  );
};

export default observer(AccountManagementDashboard);
